import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Palette, Monitor, Film, Calendar } from 'lucide-react';
import { api } from '@/lib/api';
import type { CinemaSeatType, CinemaScreen, CinemaFilm } from '@/types';

export default function CinemaConfig() {
  const { venueId } = useParams();
  const navigate = useNavigate();

  const { data: venue } = useQuery({
    queryKey: ['venue', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}`);
      return response.data;
    },
  });

  const { data: seatTypes } = useQuery<CinemaSeatType[]>({
    queryKey: ['cinema-seat-types', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/seat-types`);
      return response.data;
    },
  });

  const { data: screens } = useQuery<CinemaScreen[]>({
    queryKey: ['cinema-screens', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/screens`);
      return response.data;
    },
  });

  const { data: films } = useQuery<CinemaFilm[]>({
    queryKey: ['cinema-films', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/films`);
      return response.data;
    },
  });

  const configCards = [
    {
      title: 'Seat Types',
      description: 'Configure seat categories like Premium, Standard, Accessible',
      icon: Palette,
      count: seatTypes?.length || 0,
      countLabel: 'types',
      link: `/venues/${venueId}/cinema/seat-types`,
      color: 'bg-purple-100 text-purple-600',
    },
    {
      title: 'Screens',
      description: 'Set up screens with sections and seat layouts',
      icon: Monitor,
      count: screens?.length || 0,
      countLabel: 'screens',
      link: `/venues/${venueId}/cinema/screens`,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      title: 'Films',
      description: 'Manage films and their details',
      icon: Film,
      count: films?.length || 0,
      countLabel: 'films',
      link: `/venues/${venueId}/cinema/films`,
      color: 'bg-green-100 text-green-600',
    },
    {
      title: 'Showings',
      description: 'Schedule film screenings across screens',
      icon: Calendar,
      count: films?.reduce((acc, f) => acc + (f.showing_count || 0), 0) || 0,
      countLabel: 'showings',
      link: `/venues/${venueId}/cinema/showings`,
      color: 'bg-orange-100 text-orange-600',
    },
  ];

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/booking-config`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cinema Configuration</h1>
          <p className="text-sm text-gray-500">{(venue as Record<string, unknown>)?.name as string}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {configCards.map((card) => (
          <Link
            key={card.title}
            to={card.link}
            className="card p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-lg ${card.color}`}>
                <card.icon className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{card.title}</h3>
                <p className="text-sm text-gray-500 mb-3">{card.description}</p>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {card.count} {card.countLabel}
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
